/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { toast } from "../components/ui/sonner";
import { ShieldCheck, Loader2, ArrowRight, CheckCircle2, Clock, BellRing, BellOff } from "lucide-react";
import { fmtD, fmtDT } from "../lib/date";

const money = (n) => (n || n === 0 ? "$" + Number(n).toLocaleString() : "-");

export default function PriceApprovals() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/crm/price-approvals?status=${tab}`);
      setItems(data.items || []);
      setCounts({ pending: data.pending || 0, total: data.total || 0 });
    } catch { toast.error(zh ? "加载失败" : "Load failed"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  const approve = async (prid) => {
    setApproving(prid);
    try { await api.post(`/crm/estimate/${prid}/approve-price`); toast.success(zh ? "已批准，销售可发送业主" : "Approved"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "批准失败" : "Failed")); }
    setApproving(null);
  };

  const T = (z, e) => (zh ? z : e);

  return (
    <div className="space-y-5" data-testid="price-approvals-page">
      <div className="flex-wrap md:flex-nowrap flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-brand-500" />
        <h1 className="text-2xl font-bold text-slate-900">{T("改价审批", "Price-Change Approvals")}</h1>
      </div>

      <div className="flex-wrap md:flex-nowrap flex gap-2">
        <button data-testid="pa-tab-pending" onClick={() => setTab("pending")}
          className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`text-sm rounded-full px-4 py-1.5 border ${tab === "pending" ? "bg-brand-500 text-white border-brand-500" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`)}>
          {T("待审批", "Pending")}{counts.pending > 0 && ` (${counts.pending})`}
        </button>
        <button data-testid="pa-tab-all" onClick={() => setTab("all")}
          className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`text-sm rounded-full px-4 py-1.5 border ${tab === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`)}>
          {T("全部历史", "All history")}
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
          {tab === "pending" ? T("暂无待审批的改价", "No pending price changes") : T("暂无改价记录", "No price-change records")}
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => (
            <div key={it.processId} data-testid={`pa-row-${it.processId}`} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800">{it.clientName || "-"}</span>
                    <span className="text-[12px] text-slate-400">{it.address}</span>
                    <span className={`text-[11px] rounded-full px-2 py-0.5 font-medium ${it.approvalLevel === "vp" ? "bg-purple-100 text-purple-700" : "bg-sky-100 text-sky-700"}`} data-testid={`pa-level-${it.processId}`}>
                      {it.approvalLevel === "vp" ? T("总监级审批", "VP-level") : T("经理级审批", "Manager-level")}{it.maxDeviationPct != null ? ` · ${it.maxDeviationPct}%` : ""}
                    </span>
                    {it.approved
                      ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium" data-testid={`pa-approved-${it.processId}`}><CheckCircle2 className="h-3.5 w-3.5" />{T("已批准", "Approved")}{it.priceApprovedBy ? `·${it.priceApprovedBy}` : ""}</span>
                      : <span className="inline-flex items-center gap-1 text-[11px] text-red-600 font-medium" data-testid={`pa-pending-${it.processId}`}><Clock className="h-3.5 w-3.5" />{T("待审批", "Pending")}</span>}
                    {it.approvalLevel === "vp" && it.groupNotified != null && (
                      <span data-testid={`pa-group-${it.processId}`} title={it.groupNotifiedAt ? fmtDT(it.groupNotifiedAt) : ""}
                        className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 font-medium ${it.groupNotified ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {it.groupNotified ? <BellRing className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
                        {it.groupNotified ? T("已推送群审批", "Pushed to group") : T("群通知未送达", "Group not notified")}
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-slate-500 mt-0.5">
                    {T("改价人", "By")}: <b>{it.changedBy || "-"}</b> · {T("负责销售", "Sales")}: {it.assignedSales || "-"} · {fmtD(it.createdAt)} · {T("总价", "Total")} <b className="text-red-600">{money(it.total)}</b>
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(it.adjustments || []).map((a, i) => (
                      <span key={i} className="text-[11px] bg-amber-50 text-amber-700 rounded px-2 py-0.5">
                        {(a.name || "").split(" ")[0]}: {money(a.recommended)} → <b>{money(a.actual)}</b>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex-wrap md:flex-nowrap flex items-center gap-2 shrink-0">
                  {!it.approved && (
                    <Button size="sm" data-testid={`pa-approve-${it.processId}`} onClick={() => approve(it.processId)} disabled={approving === it.processId}
                      className="h-8 bg-brand-500 hover:bg-brand-600 text-white text-xs">
                      {approving === it.processId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : T("批准", "Approve")}
                    </Button>
                  )}
                  <button data-testid={`pa-goto-${it.processId}`} onClick={() => nav(`/crm/leads/${it.leadId}?tab=process`)}
                    className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 h-8 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 border border-indigo-200 rounded-lg px-2.5">
                    {T("查看线索", "View lead")}<ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
